import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

export interface PerformanceMetric {
  page_name: string;
  load_time_ms: number;
  memory_usage_mb: number;
}

export interface SystemAlert {
  severity: 'critical' | 'warning' | 'info';
  message: string;
  source: string;
}

class MonitoringService {
  private static instance: MonitoringService;
  private performanceThresholds = {
    loadTime: 3000, // 3 segundos
    memoryUsage: 500, // 500MB
    routeChangeTime: 1000, // 1 segundo
    apiResponseTime: 2000 // 2 segundos
  };

  private constructor() {
    window.addEventListener('error', this.handleGlobalError.bind(this));
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    this.initPerformanceMonitoring();
  }

  private initPerformanceMonitoring() {
    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      const longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.duration > 50) { // Tasks longer than 50ms
            this.createSystemAlert({
              severity: 'warning',
              message: `Long task detected: ${Math.round(entry.duration)}ms`,
              source: 'performance_monitoring'
            });
          }
        });
      });

      longTaskObserver.observe({ entryTypes: ['longtask'] });
    }

    // Monitor memory usage
    this.monitorMemoryUsage();
  }

  private async monitorMemoryUsage() {
    if ('memory' in performance) {
      setInterval(async () => {
        const memory = (performance as any).memory;
        if (memory.usedJSHeapSize > this.performanceThresholds.memoryUsage * 1024 * 1024) {
          await this.createSystemAlert({
            severity: 'warning',
            message: `High memory usage detected: ${Math.round(memory.usedJSHeapSize / (1024 * 1024))}MB`,
            source: 'memory_monitoring'
          });
        }
      }, 30000); // Check every 30 seconds
    }
  }

  private async handleGlobalError(event: ErrorEvent) {
    await this.logError('runtime_error', event.error?.message || event.message, {
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  }

  private async handleUnhandledRejection(event: PromiseRejectionEvent) {
    await this.logError('unhandled_promise', event.reason?.message || 'Promise rejected', {
      stack: event.reason?.stack,
      type: event.reason?.name
    });
  }

  public static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }

  async logError(type: string, description: string, metadata: Record<string, any> = {}) {
    try {
      const { error } = await supabase
        .from('monitoring_logs')
        .insert([{ 
          type: 'error', 
          description,
          metadata: metadata
        }]);

      if (error) throw error;

      // Create system alert for critical errors
      if (type === 'runtime_error' || type === 'unhandled_promise') {
        await this.createSystemAlert({
          severity: 'critical',
          message: `Critical error detected: ${description}`,
          source: 'error_monitoring'
        });
      }
    } catch (err) {
      console.error('Failed to log error:', err);
    }
  }

  async trackPagePerformance(metric: PerformanceMetric) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('User not authenticated, skipping performance tracking');
        return;
      }

      const { error } = await supabase
        .from('performance_metrics')
        .insert([{ ...metric, user_id: user.id }]);

      if (error) throw error;

      // Check thresholds and create alerts
      if (metric.load_time_ms > this.performanceThresholds.loadTime) {
        await this.createSystemAlert({
          severity: 'warning',
          message: `High page load time (${metric.load_time_ms}ms) detected on ${metric.page_name}`,
          source: 'performance_monitoring'
        });
      }

      if (metric.memory_usage_mb > this.performanceThresholds.memoryUsage) {
        await this.createSystemAlert({
          severity: 'warning',
          message: `High memory usage (${metric.memory_usage_mb}MB) detected on ${metric.page_name}`,
          source: 'performance_monitoring'
        });
      }

      console.log('Performance metric tracked:', metric);
    } catch (error: any) {
      console.error('Error tracking performance:', error);
      this.logError('performance_tracking', error.message);
      toast({
        title: "Erro",
        description: "Falha ao registrar métrica de performance",
        variant: "destructive",
      });
    }
  }

  async createSystemAlert(alert: SystemAlert) {
    try {
      const { error } = await supabase
        .from('system_alerts')
        .insert([alert]);

      if (error) throw error;

      // Show toast for critical alerts
      if (alert.severity === 'critical') {
        toast({
          title: "Alerta Crítico",
          description: alert.message,
          variant: "destructive",
        });
      }

      console.log('System alert created:', alert);
    } catch (error: any) {
      console.error('Error creating alert:', error);
      this.logError('alert_creation', error.message);
    }
  }

  async getRecentAlerts() {
    try {
      const { data, error } = await supabase
        .from('system_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching alerts:', error);
      return [];
    }
  }

  async resolveAlert(alertId: string) {
    try {
      const { error } = await supabase
        .from('system_alerts')
        .update({
          is_resolved: true,
          resolved_at: new Date().toISOString()
        })
        .eq('id', alertId);

      if (error) throw error;

      toast({
        title: "Alerta Resolvido",
        description: "O alerta foi marcado como resolvido",
      });
    } catch (error: any) {
      console.error('Error resolving alert:', error);
      toast({
        title: "Erro",
        description: "Falha ao resolver alerta",
        variant: "destructive",
      });
    }
  }
}

export const monitoringService = MonitoringService.getInstance();